import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, debounceTime, distinctUntilChanged, firstValueFrom, map, takeUntil, tap } from 'rxjs';
import { Especialidad } from 'src/app/models/Especialidad';
import { UsuarioService } from 'src/app/services/usuario.service'; 
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { normalizeText } from 'src/app/shared/text.util';

@Component({
    selector: 'app-especialidades',
    templateUrl: './especialidades.component.html',
    styleUrls: ['./especialidades.component.css'],
    standalone: false
})
export class EspecialidadesComponent implements OnInit {
  private router = inject(Router);
  private _location = inject(Location);
  private _usuarioService = inject(UsuarioService);
  private _authService = inject(AuthService);
  private _loadingService = inject(LoadingService);
  especialidades: Especialidad[] = [];
  especialidadesFiltradas$?: Observable<Especialidad[]>;
  searchTerm$ = new BehaviorSubject<string>('');
  error = signal<string>('');
  message = signal<string>('');
  loading = computed(() => this._loadingService.isLoading());
  private OnDestroy$ = new Subject();

  constructor(){

  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    // Si necesitás (re)cargar usuario/cliente desde backend, hacelo bajo el loader:
    await this._loadingService.run(async () => {
      try {
        // Asegura bootstrap de sesión (idempotente, ok en web/nativo)
        await this._authService.ensureSession();
        const res = await firstValueFrom(this._usuarioService.getEspecialidades().pipe(
          tap(() => console.log('Especialidades obtenidas correctamente')))
        );

        this.especialidades = res;
        this.filterList();
      } catch (e: any) {
        console.error(e);
        this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
        this.router.navigate(['/sign-in']);
      }
    });
  }

  filterList(): void {
    const collator = new Intl.Collator('es', {
      sensitivity: 'base',
      ignorePunctuation: true,
    });

    this.especialidadesFiltradas$ = this.searchTerm$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      takeUntil(this.OnDestroy$),
      map(term => {
        const q = normalizeText(term);

        return (this.especialidades ?? [])
          .filter(e => {
            const descripcion = normalizeText(e.x_descripcion);
            const tags = normalizeText(e.x_tags ?? '');

            return (
              descripcion.includes(q) ||
              tags.includes(q)
            );
          })
          .sort((a, b) =>
            collator.compare(a.x_descripcion ?? '', b.x_descripcion ?? '')
          );
      })
    );
  }

  /**
   * Activa/Desactiva un usuario.
   * Recibe el modelo y el nuevo estado (true/false).
   * Hace "optimistic update" y revierte si hay error.
   */
  async activarDesactivar(especialidad: Especialidad, checked: boolean): Promise<void> {
    // Guardá el valor previo antes de cambiarlo
    const prev = !!especialidad.b_activo;

    // Optimistic UI: reflejar el cambio en la UI al toque
    especialidad.b_activo = checked;

    try {
      await this._loadingService.run(async () => {
        await firstValueFrom(this._usuarioService.updateEspecialidad(especialidad));
      });
      this.message.set('Modificación realizada correctamente');
      setTimeout(() => this.message.set(''), 2500);
    } catch (e) {
      // Rollback si falla
      especialidad.b_activo = prev;
      this.error.set('Error al realizar la modificación');
      console.error(e);
    }
  }

  back(){
    this._location.back();
  }
}
